/**
 * Central annonskonfiguration — EN källa för master-brytare, läge och slot-ID:n.
 *
 * Allt styrs av miljövariabler och är AVSTÄNGT som standard. Dagen AdSense
 * godkänner sajten räcker det att fylla i slot-ID:n och sätta
 * VITE_ADSENSE_ENABLED=true — ingen kodändring behövs.
 *
 * Miljövariabler:
 *   VITE_ADSENSE_ENABLED   "true" | "false"                (default false)
 *   VITE_ADSENSE_MODE      "manual" | "auto" | "hybrid"    (default manual)
 *   VITE_ADSENSE_SLOT_INARTICLE
 *   VITE_ADSENSE_SLOT_INARTICLE2
 *   VITE_ADSENSE_SLOT_BOTTOM
 *   VITE_ADSENSE_SLOT_MULTIPLEX
 *   VITE_ADSENSE_SLOT_STICKY
 *   VITE_ADSENSE_SLOT_INFEED
 *   VITE_ADSENSE_SLOT_HORIZONTAL
 *   VITE_ADSENSE_SLOT_MOBILE_FIXED
 */

export type AdMode = "off" | "manual" | "auto" | "hybrid";

export type AdVariant =
  | "in-article"
  | "in-article-2"
  | "in-feed"
  | "display"
  | "display-horizontal"
  | "mobile-fixed"
  | "multiplex"
  | "sticky";

const env = (key: string): string => {
  const v = (import.meta.env as Record<string, unknown>)[key];
  return typeof v === "string" ? v.trim() : "";
};

/** Master-brytare. Inga annonser laddas alls när denna är av. */
export const ADS_ENABLED = env("VITE_ADSENSE_ENABLED").toLowerCase() === "true";

/** Läge: manuella slots, Auto Ads, eller båda (rekommenderat: hybrid). */
export const AD_MODE: AdMode = (() => {
  if (!ADS_ENABLED) return "off";
  const m = env("VITE_ADSENSE_MODE").toLowerCase();
  // Default = manual. Auto Ads (page-level) kan visa helsidesannonser
  // (vignette/interstitial) vid sidbyten, vilket vi inte vill ha.
  return m === "manual" || m === "auto" || m === "hybrid" ? (m as AdMode) : "manual";
})();

export const AUTO_ADS_ON = AD_MODE === "auto" || AD_MODE === "hybrid";
export const MANUAL_ADS_ON = AD_MODE === "manual" || AD_MODE === "hybrid";

/** Slot-ID:n per variant. Tom sträng = placeringen renderas inte. */
export const AD_SLOTS: Record<AdVariant, string> = {
  "in-article": env("VITE_ADSENSE_SLOT_INARTICLE"),
  "in-article-2": env("VITE_ADSENSE_SLOT_INARTICLE2") || env("VITE_ADSENSE_SLOT_INARTICLE"),
  "in-feed": env("VITE_ADSENSE_SLOT_INFEED") || env("VITE_ADSENSE_SLOT_INARTICLE"),
  display: env("VITE_ADSENSE_SLOT_BOTTOM"),
  "display-horizontal": env("VITE_ADSENSE_SLOT_HORIZONTAL"),
  "mobile-fixed": env("VITE_ADSENSE_SLOT_MOBILE_FIXED"),
  multiplex: env("VITE_ADSENSE_SLOT_MULTIPLEX"),
  sticky: env("VITE_ADSENSE_SLOT_STICKY"),
};

/** Reserverad minsta höjd (px) så layouten inte hoppar (CLS). */
export const AD_RESERVED_HEIGHT: Record<AdVariant, number> = {
  "in-article": 280,
  "in-article-2": 280,
  "in-feed": 250,
  display: 280,
  "display-horizontal": 280,
  "mobile-fixed": 100,
  multiplex: 420,
  sticky: 600,
};

export function slotFor(variant: AdVariant, explicit?: string): string {
  return (explicit || AD_SLOTS[variant] || "").trim();
}

/** Sammanfattning för diagnostiksidan. */
export function adsConfigSummary() {
  return {
    enabled: ADS_ENABLED,
    mode: AD_MODE,
    autoAds: AUTO_ADS_ON,
    manualAds: MANUAL_ADS_ON,
    slots: AD_SLOTS,
  };
}
